import { useEffect, useCallback } from 'react';

declare global {
  interface Window {
    grecaptcha?: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY;

/**
 * Custom hook for Google reCAPTCHA v3 integration
 * @returns Object with executeRecaptcha function to get tokens
 */
export function useRecaptcha() {
  useEffect(() => {
    // Skip loading if no site key is configured
    if (!RECAPTCHA_SITE_KEY) {
      console.warn('reCAPTCHA site key not configured');
      return;
    }

    // Check if script is already loaded
    if (document.getElementById('recaptcha-script')) {
      return;
    }

    // Load reCAPTCHA script
    const script = document.createElement('script');
    script.id = 'recaptcha-script';
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);

    return () => {
      // Cleanup: remove script and grecaptcha badge on unmount
      const scriptElement = document.getElementById('recaptcha-script');
      if (scriptElement) {
        scriptElement.remove();
      }
      const badge = document.querySelector('.grecaptcha-badge');
      if (badge) {
        badge.remove();
      }
    };
  }, []);

  /**
   * Execute reCAPTCHA and get a token
   * @param action - The action name (e.g., 'login', 'register')
   * @returns Promise<string | null> - The reCAPTCHA token or null if unavailable
   */
  const executeRecaptcha = useCallback(
    async (action: string): Promise<string | null> => {
      if (!RECAPTCHA_SITE_KEY) {
        console.warn('reCAPTCHA site key not configured, skipping verification');
        return null;
      }

      return new Promise((resolve) => {
        if (!window.grecaptcha) {
          console.error('reCAPTCHA not loaded');
          resolve(null);
          return;
        }

        window.grecaptcha.ready(() => {
          window.grecaptcha!
            .execute(RECAPTCHA_SITE_KEY, { action })
            .then((token) => {
              resolve(token);
            })
            .catch((error) => {
              console.error('reCAPTCHA execution error:', error);
              resolve(null);
            });
        });
      });
    },
    []
  );

  return { executeRecaptcha };
}
